// @owners { team: patient-health }
import { SPACING } from '@alto/design-library-tokens';
// eslint-disable-next-line import/no-deprecated
import { getScreenSize } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { Button, FlexColumn, LoadingButton, Text, toaster } from '@alto/pocky';
import autobind from 'autobind-decorator';
import React from 'react';
import styled from 'styled-components';
import { inactiveText } from '~shared/features/my-meds/helper';
import { getErrorMessage } from '~shared/helpers/helper';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { type Event } from '~shared/lib/analytics/src/types';
import { type APIError } from '~shared/types/APIError';
import { type Prescription } from '~shared/types/clients';
import { InactiveContainer } from './PrescriptionInactive';

const UnarchiveContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  > *:not(:last-child) {
    margin-right: ${SPACING.STATIC.MD.rem};
  }
`;

const ButtonWrapper = styled(FlexColumn)<{ isSMScreenOrBigger: boolean }>`
  ${({ isSMScreenOrBigger }) =>
    !isSMScreenOrBigger &&
    `
      width: 100%;
      margin-top: ${SPACING.STATIC.MD.rem};
      > * {
        width: 100%;
      }
      > ${UnarchiveContainer} {
        flex-direction: column-reverse;
        > * {
          margin-right: 0;
        }
        > *:not(:first-child) {
          margin-bottom: ${SPACING.STATIC.MD.rem};
        }
      }
    `}
`;

const confirmingUnarchiveText =
  'Unarchiving this prescription will restore it to the active section of your medication list.';

type Props = {
  readonly prescription: Prescription;
  readonly unarchivePrescription: (arg0: Prescription) => void;
  readonly unarchivingPrescription: boolean;
  readonly unarchivePrescriptionError: APIError | null | undefined;
  readonly sendAnalyticsEvent: (event: Event) => void;
};

type State = {
  confirmingUnarchive: boolean;
};

export default class PrescriptionInactiveArchived extends React.PureComponent<Props, State> {
  static defaultProps = {
    unarchivePrescriptionError: null,
  };

  state = {
    confirmingUnarchive: false,
  };

  UNSAFE_componentWillReceiveProps({ unarchivePrescriptionError }: Props) {
    const { unarchivePrescriptionError: lastUnarchiveError } = this.props;
    const message = getErrorMessage(unarchivePrescriptionError);

    if (!lastUnarchiveError && message) {
      toaster.toast({
        description: 'Please contact us if this error persists.',
        kind: 'danger',
        title: message,
      });
    }
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  toggleConfirmUnarchive() {
    const { prescription, sendAnalyticsEvent } = this.props;
    const { confirmingUnarchive } = this.state;

    this.setState((prevState) => ({
      confirmingUnarchive: !prevState.confirmingUnarchive,
    }));

    if (!confirmingUnarchive) {
      sendAnalyticsEvent(
        createEvent(EVENTS.MED_DETAILS__UNARCHIVE_CONFIRMATION_VIEWED, {}, { prescriptionId: prescription.id }),
      );
    }
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  unarchivePrescription() {
    const { unarchivePrescription, prescription } = this.props;
    unarchivePrescription(prescription);
  }

  render() {
    const { prescription, unarchivingPrescription } = this.props;
    const { confirmingUnarchive } = this.state;
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger } = getScreenSize();

    return (
      <InactiveContainer isSMScreenOrBigger={isSMScreenOrBigger}>
        <div>
          <Text>{confirmingUnarchive ? confirmingUnarchiveText : inactiveText(prescription)}</Text>
        </div>
        <ButtonWrapper isSMScreenOrBigger={isSMScreenOrBigger}>
          {confirmingUnarchive ? (
            <UnarchiveContainer>
              <Button
                kind="secondary"
                // eslint-disable-next-line @typescript-eslint/unbound-method
                onClick={this.toggleConfirmUnarchive}
              >
                Cancel
              </Button>

              <LoadingButton
                loading={unarchivingPrescription}
                loadingText="Unarchiving..."
                // eslint-disable-next-line @typescript-eslint/unbound-method
                onClick={this.unarchivePrescription}
              >
                Unarchive
              </LoadingButton>
            </UnarchiveContainer>
          ) : (
            <Button
              kind="secondary"
              // eslint-disable-next-line @typescript-eslint/unbound-method
              onClick={this.toggleConfirmUnarchive}
            >
              Unarchive this Prescription
            </Button>
          )}
        </ButtonWrapper>
      </InactiveContainer>
    );
  }
}
